import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, effect, Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import type { AuthenticationRequestModel } from '../../../../server/controllers/models/authentication-request.model';
import type { AuthenticationResponseModel } from '../../../../server/controllers/models/authentication-response.model';
import type { UserResponseModel } from '../../../../server/controllers/models/user-response.model';
import { ErrorDialogService } from '../../error/error-dialog.service';
import type { UserItem } from '../../models/middleware/user-item.model';
import { MicrosoftTeamsIntegrationService } from '../../services/microsoft-teams-integration.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly AUTH_TOKEN_KEY = 'AuthToken';

  readonly currentUser = signal<UserItem | null>(null);
  readonly isInitializing = signal<boolean>(true);
  readonly isAuthenticated = computed(() => !!this.currentUser() && !this.isInitializing());

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly microsoftTeamsIntegrationService = inject(MicrosoftTeamsIntegrationService);
  private readonly errorService = inject(ErrorDialogService);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      this.isInitializing.set(false);
      return;
    }

    if (AuthService.getAuthToken()) {
      this.validateTokenAndSetUser().then(() => this.isInitializing.set(false));
      return;
    }

    const waitUntilInitialized = effect(() => {
      setTimeout(() => {
        const isInitializing = this.microsoftTeamsIntegrationService.isInitializing();
        const isMicrosoftTeamsEnvironment = this.microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment();

        console.log(`microsoftTeamsIntegrationService.isInitializing() = ${isInitializing}`);
        console.log(`microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment() = ${isMicrosoftTeamsEnvironment}`);

        if (!isInitializing) {
          console.log('choosing login mode');
          this.isInitializing.set(false);
          if (isMicrosoftTeamsEnvironment) {
            console.log('login mode: SSO');
            this.loginWithSingleSignOn().then();
          } else {
            console.log('login mode: federated authentication');
          }

          waitUntilInitialized.destroy();
        }
      }, 1000);
    });
  }

  public static getAuthToken(): string | null {
    return localStorage.getItem(AuthService.AUTH_TOKEN_KEY);
  }

  private static setAuthToken(token: string): void {
    localStorage.setItem(AuthService.AUTH_TOKEN_KEY, token);
  }

  private static removeAuthToken(): void {
    localStorage.removeItem(AuthService.AUTH_TOKEN_KEY);
  }

  private async validateTokenAndSetUser(): Promise<void> {
    const userResponseModel = await firstValueFrom(
      this.http.get<UserResponseModel>('/api/authenticated/users/me').pipe(
        catchError((error) => {
          console.warn('Error validating token: ', error);
          AuthService.removeAuthToken();
          this.errorService.openDialog(error.message, error.status);
          return of(null);
        })
      )
    );
    this.currentUser.set(userResponseModel);
  }

  private async loginWithSingleSignOn(): Promise<void> {
    try {
      const token = await this.microsoftTeamsIntegrationService.getToken();
      const authenticationRequestModel: AuthenticationRequestModel = {
        token: token,
      };
      console.log('sso authentication started');
      const authenticationResponseModel = await firstValueFrom(
        this.http.post<AuthenticationResponseModel>('/api/public/authentication/sso', authenticationRequestModel)
      );
      console.log('sso authentication finished');

      AuthService.setAuthToken(authenticationResponseModel.token);
      this.currentUser.set(authenticationResponseModel.user);
      console.log(`User: ${JSON.stringify(this.currentUser())}`);
      // await this.router.navigate(['home']);
      window.location.href = '/home';
      console.log('here we go');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(errorMessage);
      // await this.router.navigate(['login'], {
      //   onSameUrlNavigation: 'reload',
      //   queryParams: {
      //     error: 'Authentication with SSO failed.',
      //     cause: errorMessage,
      //   },
      // });
      const params = new URLSearchParams({
        error: 'Authentication with SSO failed.',
        cause: errorMessage,
      });
      this.errorService.openDialog(errorMessage, (error as unknown as { status?: number })?.status);
      window.location.href = `/login?${params.toString()}`;
    }
  }

  public async loginWithFederatedAuthentication(token: string): Promise<void> {
    try {
      const authenticationRequestModel: AuthenticationRequestModel = {
        token: token,
      };
      const authenticationResponseModel = await firstValueFrom(
        this.http.post<AuthenticationResponseModel>('/api/public/authentication/federated', authenticationRequestModel)
      );

      AuthService.setAuthToken(authenticationResponseModel.token);
      this.currentUser.set(authenticationResponseModel.user);
      await this.router.navigate(['home']);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(errorMessage);
      await this.router.navigate(['login'], {
        onSameUrlNavigation: 'reload',
        queryParams: {
          error: 'Federated authentication failed.',
          cause: errorMessage,
        },
      });
    }
  }

  public loginWithStrapiToken(token: string): Promise<void> {
    AuthService.setAuthToken(token);
    return this.validateTokenAndSetUser();
  }

  public initiateFederatedAuthentication(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment()) return;

    // TODO: check if we can use middleware as proxy
    window.location.href = `${environment.strapiBaseUrl}/api/connect/microsoft`;
  }

  public async logout(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment()) return;

    AuthService.removeAuthToken();
    this.currentUser.set(null);
    this.router.navigate(['login']).then();
  }
}
